import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getWOReview, signWO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SignaturePad } from "@/components/SignaturePad";
import { CheckCircle, XCircle, Wrench, Loader2, Download } from "lucide-react";
import { downloadPdfUrl } from "@/lib/downloadBlob";
import { COMPANY_INFO } from "@/lib/companyInfo";
import { rollUpLineItems, applyCCFeeToRolledItems } from "@/lib/rollUpLineItems";

const fmt = (v) => Number(v || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

export default function WOReviewPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [signedBy, setSignedBy] = useState("");

  useEffect(() => {
    document.title = "ServiceOps Work Order";
    (async () => {
      try {
        const res = await getWOReview(token);
        setData(res.data);
        const woNum = res.data?.wo?.wo_number;
        document.title = woNum ? `ServiceOps Work Order — ${woNum}` : "ServiceOps Work Order";
        if (res.data.wo.status === "approved") setSigned(true);
      } catch {
        setError("This work order link is invalid or has expired.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleSign = async (signatureData) => {
    setSigning(true);
    try {
      await signWO(token, { signature_data: signatureData, signed_by: signedBy });
      setSigned(true);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to sign work order");
    } finally {
      setSigning(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
        <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-slate-800 mb-2">Link Not Found</h2>
        <p className="text-sm text-slate-500">{error}</p>
      </div>
    </div>
  );

  const { wo, client_name, site_name, site_address, contact_name, contact_email, contact_phone } = data;

  // Pricing math — must match the in-app WO View / executed PDF exactly so
  // the customer doesn't see a different total when they receive the
  // emailed/web review vs. the PDF. Same rules:
  //   - rollUpLineItems: consolidate freight/labor into the matching part
  //     row so the customer sees one line per item (not 3 separate slices)
  //   - applyCCFeeToRolledItems: silently distribute the CC fee into
  //     each row's Material/Total so the line-item sum reconciles with
  //     the Subtotal label below
  //   - tax base = (material rows) + cc_fee_amount
  //   - total = subtotal_with_fee + tax
  const ccFeePct = wo.cc_fee_pct == null ? 0 : (parseFloat(wo.cc_fee_pct) || 0);
  const showBreakdown = !!wo.show_breakdown;
  const baseRolled = rollUpLineItems(wo.line_items || [], showBreakdown);
  const displayItems = applyCCFeeToRolledItems(baseRolled, ccFeePct);
  const baseSubtotal = (wo.line_items || []).reduce((s, li) => s + (Number(li.quantity) || 0) * (Number(li.unit_price) || 0), 0);
  // #244 (iter168): approved Change Orders must roll into the customer-
  // facing subtotal/total — otherwise the signer is approving a number
  // that doesn't match what the WO actually bills (and the executed PDF
  // shows). Mirror WorkOrderTab.jsx::revisedSubtotal.
  const approvedCOs = (wo.change_orders || []).filter(co => (co.status || "").toLowerCase() === "approved");
  const coSubtotal = approvedCOs.reduce((s, co) => s + (Number(co.total) || Number(co.subtotal) || 0), 0);
  const ccFeeAmount = Math.round(baseSubtotal * (ccFeePct / 100) * 100) / 100;
  const subtotalWithFee = baseSubtotal + ccFeeAmount + coSubtotal;
  const isMaterialCt = (ct) => { const s = String(ct || "").toLowerCase(); return s === "2" || s === "material"; };
  const materialOnly = (wo.line_items || []).reduce((m, li) => m + (isMaterialCt(li.cost_type) ? (Number(li.quantity) || 0) * (Number(li.unit_price) || 0) : 0), 0);
  const taxableBase = materialOnly + ccFeeAmount;
  const taxAmount = taxableBase * ((wo.tax_rate || 0) / 100);
  const total = subtotalWithFee + taxAmount;

  if (signed) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center w-full">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-slate-800 mb-2">Work Order Approved</h2>
        <p className="text-sm text-slate-500 mb-5">Thank you for approving this work order. {COMPANY_INFO.name} will schedule the work and be in touch.</p>
        {wo?.id && (
          <button
            type="button"
            onClick={() => downloadPdfUrl(`/work-orders/${wo.id}/executed-pdf?download=1`, {
              suggestedName: `ABS_Work_Order_${wo.wo_number || wo.id}_Signed.pdf`,
              label: "signed work order",
            })}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-slate-800 text-white hover:bg-slate-900 text-sm font-medium shadow"
            data-testid="btn-download-signed-wo"
          >
            <Download className="w-4 h-4" /> Download Signed Work Order
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header strip — logo top-left, copy stacked to the right of it. */}
          <div className="bg-slate-800 text-white p-6 flex items-center gap-4">
            <img src="/abs-logo.png" alt={COMPANY_INFO.name} className="abs-logo h-14 w-14 object-contain bg-white rounded p-1 shrink-0" data-testid="wo-review-logo" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium opacity-70 mb-1">{COMPANY_INFO.name}</div>
              <h1 className="text-xl font-bold">Work Order for Review & Approval</h1>
              <div className="text-sm opacity-80 mt-1">{wo.wo_number} — {wo.title}</div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Details */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-slate-400">Customer:</span> <span className="font-medium">{client_name}</span></div>
              {site_name && <div><span className="text-slate-400">Site:</span> <span className="font-medium">{site_name}</span></div>}
              {site_address && <div className="col-span-2"><span className="text-slate-400">Address:</span> <span className="font-medium">{site_address}</span></div>}
              {contact_name && (
                <div className="col-span-2" data-testid="wo-review-contact">
                  <span className="text-slate-400">Contact:</span>{" "}
                  <span className="font-medium">{contact_name}</span>
                  {contact_email && <> · <a href={`mailto:${contact_email}`} className="text-blue-600 hover:underline">{contact_email}</a></>}
                  {contact_phone && <> · <a href={`tel:${contact_phone.replace(/\D/g, '')}`} className="text-blue-600 hover:underline">{contact_phone}</a></>}
                </div>
              )}
              {wo.scheduled_date && <div><span className="text-slate-400">Scheduled:</span> <span className="font-medium">{wo.scheduled_date}</span></div>}
              {wo.wo_scopes?.length > 0 && <div><span className="text-slate-400">Scope:</span> <span className="font-medium">{wo.wo_scopes.join(", ")}</span></div>}
            </div>

            {/* Qualifications — customer-visible scope summary / notes
                from the WO's `customer_notes` field. Renders above the
                line items so the customer reads the qualifying details
                before pricing. Title matches the View PDF exactly. */}
            {wo.customer_notes && (
              <div data-testid="wo-review-qualifications">
                <div className="font-semibold text-sm text-slate-700 mb-1">Qualifications</div>
                <div className="bg-slate-50 border-l-4 border-slate-400 rounded p-3 text-sm whitespace-pre-wrap text-slate-700">
                  {wo.customer_notes}
                </div>
              </div>
            )}

            {/* Line Items — rolled up by part #, CC fee silently baked in
                so the on-page sum matches the Subtotal below. Mirrors
                the WO View / executed PDF exactly. */}
            {displayItems.length > 0 && (
              <div className="border rounded-lg overflow-hidden" data-testid="wo-review-line-items">
                <table className="w-full text-sm">
                  <thead className="bg-slate-700 text-white">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold w-24">Part #</th>
                      <th className="text-left px-4 py-2 font-semibold">Name</th>
                      <th className="text-center px-4 py-2 font-semibold w-16">Qty</th>
                      {showBreakdown && <th className="text-right px-4 py-2 font-semibold w-28">Material</th>}
                      {showBreakdown && <th className="text-right px-4 py-2 font-semibold w-28">Labor</th>}
                      <th className="text-right px-4 py-2 font-semibold w-28">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {displayItems.map((it, i) => (
                      // eslint-disable-next-line react/no-array-index-key -- WO review snapshot rendering; rolled-up items have no stable id
                      <tr key={`wor-${i}`} className="text-slate-700">
                        <td className="px-4 py-2 text-slate-400">{it.part_number || ""}</td>
                        <td className="px-4 py-2 font-medium">{it.name || it.description || ""}</td>
                        <td className="px-4 py-2 text-center">{it.quantity || 1}</td>
                        {showBreakdown && <td className="px-4 py-2 text-right">{fmt(it.material_total || 0)}</td>}
                        {showBreakdown && <td className="px-4 py-2 text-right">{fmt(it.labor_total || 0)}</td>}
                        <td className="px-4 py-2 text-right font-semibold">{fmt(it.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50">
                    <tr className="text-slate-700">
                      <td colSpan={showBreakdown ? 5 : 3} className="px-4 py-2 text-right font-semibold">Subtotal</td>
                      <td className="px-4 py-2 text-right font-bold">{fmt(subtotalWithFee)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* #244 (iter168): render approved Change Orders so the
                customer sees the same scope/$ rollup that's baked into
                the totals below. Each CO shows its title + total. */}
            {approvedCOs.length > 0 && (
              <div className="border rounded-lg overflow-hidden" data-testid="wo-review-change-orders">
                <table className="w-full text-sm">
                  <thead className="bg-amber-100 text-amber-900">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold">Approved Change Orders</th>
                      <th className="text-right px-4 py-2 font-semibold w-32">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {approvedCOs.map((co, i) => (
                      <tr key={co.id || i} className="text-slate-700" data-testid={`wo-review-co-${co.id || i}`}>
                        <td className="px-4 py-2 font-medium">{co.title || co.co_number || `Change Order ${i + 1}`}</td>
                        <td className="px-4 py-2 text-right font-semibold">{fmt(Number(co.total) || Number(co.subtotal) || 0)}</td>
                      </tr>
                    ))}
                    <tr className="bg-amber-50 text-amber-900">
                      <td className="px-4 py-2 text-right font-semibold">CO Subtotal</td>
                      <td className="px-4 py-2 text-right font-bold">{fmt(coSubtotal)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Totals — slate panel matching the WO View final block. */}
            <div className="flex justify-end">
              <div className="bg-slate-50 rounded-lg px-6 py-3 text-right space-y-1 min-w-[260px]" data-testid="wo-review-totals">
                <div className="flex justify-between gap-6 text-sm"><span className="text-slate-500">Original Subtotal</span><span className="tabular-nums">{fmt(subtotalWithFee)}</span></div>
                {(wo.tax_rate || 0) > 0 && (
                  <div className="flex justify-between gap-6 text-sm">
                    <span className="text-slate-500">Tax ({wo.tax_rate}% on material)</span>
                    <span className="tabular-nums">{fmt(taxAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between gap-6 text-lg font-bold border-t pt-1"><span>Total</span><span className="tabular-nums">{fmt(total)}</span></div>
              </div>
            </div>

            {/* Sign Section */}
            <div className="border-t pt-6">
              {!showSignature ? (
                <div className="text-center">
                  <p className="text-sm text-slate-500 mb-4">By signing below, you approve this work order and authorize {COMPANY_INFO.name} to proceed with the scope of work described above.</p>
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8" onClick={() => setShowSignature(true)} data-testid="btn-approve-wo">
                    <Wrench className="w-4 h-4 mr-2" /> Review & Sign
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Your Name</label>
                    <Input value={signedBy} onChange={(e) => setSignedBy(e.target.value)} placeholder="Full name" className="mt-1" data-testid="input-signed-by" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Signature</label>
                    <SignaturePad
                      onSave={(sigData) => handleSign(sigData)}
                      height={150}
                    />
                  </div>
                  {signing && <div className="text-center text-sm text-blue-500"><Loader2 className="w-4 h-4 inline animate-spin mr-1" />Submitting...</div>}
                  <Button variant="outline" size="sm" onClick={() => setShowSignature(false)}>Cancel</Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
